import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { exchangeSsoCode } from '../services/api';

/**
 * SSO callback landing page for the socket-cloud console (arc
 * socket-cloud-operator-sso; the fail-open contract deleted in sso-00, the
 * exchange wired in sso-04).
 *
 * THIS PAGE ACCEPTS NO CREDENTIAL FROM THE URL, UNDER ANY NAME. It reads one
 * parameter, `code`, which is not a credential for this console: it is a
 * single-use, audience-bound handoff reference that only socket-cloud-api can
 * redeem, over a server-to-server call, against an aaim-core store that burns
 * it on presentation. The page cannot read it as a session and cannot make one
 * from it.
 *
 * What was deleted, and must stay deleted: the page read `?token=` and
 * `?refresh=` from the query string and handed them to `AuthContext.setTokens`,
 * whose non-OK `/api/v1/auth/me` branch then dispatched LOGIN_SUCCESS with a
 * module-level fallback identity and a 24-hour session. A bare visit to
 * `/auth/callback?token=x&refresh=y` therefore rendered the console as an
 * authenticated, fabricated user. A bearer or refresh token in a query string
 * is forbidden on its own terms by `.claude/rules/cookie-session-auth-canon.md`
 * independently of what the receiving code does with it.
 *
 * THE SESSION NEVER TOUCHES PAGE JS. The exchange answers with the operator's
 * identity and no credential; the session arrives as Set-Cookie on that same
 * response. Identity is then hydrated through the AuthContext path that already
 * reads `/api/v1/auth/me` from the cookie
 * (.claude/rules/no-browser-token-storage.md).
 *
 * FAIL CLOSED, every branch. An absent code, a refused exchange, or a session
 * that /me does not resolve renders the failure state and offers the sign-in
 * page again. There is no fallback identity and no demo path.
 */
export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokens } = useAuth();

  const [failure, setFailure] = useState<string | null>(null);
  // The code is single-use: a second exchange of the same value is refused by
  // aaim-core and would turn a successful sign-in into a failure. `src/main.tsx`
  // does not mount under StrictMode today, so the double-invoked mount effect is
  // not the live hazard -- a re-render that re-runs the effect is, and enabling
  // StrictMode later must not silently break sign-in.
  const startedRef = useRef(false);

  // `useSearchParams` is read once at mount rather than listed as an effect
  // dependency: the code is consumed by the first run, and re-running on a
  // query-string change would re-present a burned value.
  const code = searchParams.get('code');

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    if (typeof code !== 'string' || code.length === 0) {
      setFailure(
        'This console does not accept sign-in credentials from a link. Start again from the sign-in page.',
      );
      return;
    }

    const complete = async (): Promise<void> => {
      try {
        // Same-origin, cookies included. The response body carries the operator
        // identity; the session is the Set-Cookie on that response.
        await exchangeSsoCode(code);
        // Hydrate the identity from /api/v1/auth/me through the cookie. This is
        // the only thing that establishes authenticated state, and it dispatches
        // LOGIN_FAILURE rather than any fallback identity when /me does not
        // resolve a session. The two empty arguments are the unused parameters
        // of the `setTokens` signature declared on IAuthContext -- the console
        // has no token to pass, which is the point.
        await setTokens('', '');
        // react-router v7's navigate returns a promise; nothing here depends on
        // its completion.
        void navigate('/', { replace: true });
      } catch (error) {
        setFailure(
          error instanceof Error && error.message
            ? error.message
            : 'The sign-in could not be completed. Start again from the sign-in page.',
        );
      }
    };

    void complete();
    // Mount-only, deliberately: the code is single-use, so re-running this on a
    // dependency change would re-present a value aaim-core has already burned.
    // `code`, `setTokens` and `navigate` are read from the first render and the
    // ref above makes a second run a no-op regardless.
  }, []);

  if (failure === null) {
    return (
      <div className="min-h-screen bg-console-bg flex items-center justify-center p-4">
        <div className="text-center" data-testid="auth-callback-pending">
          <div className="w-8 h-8 border-2 border-console-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-console-muted">Completing sign-in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-console-bg flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-console-card border border-console-border rounded-xl shadow-2xl p-8 max-w-md w-full text-center"
        data-testid="auth-callback-unauthenticated"
      >
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-console-text mb-2">
          Sign in could not be completed
        </h2>
        <p className="text-console-muted mb-6" data-testid="auth-callback-failure-reason">
          {failure}
        </p>
        <button
          onClick={() => void navigate('/login', { replace: true })}
          className="px-6 py-2 bg-console-accent hover:bg-console-accent/90
                   text-white font-medium rounded-lg transition-colors duration-200"
        >
          Back to Login
        </button>
      </motion.div>
    </div>
  );
};
